import React, {
  Children,
  cloneElement,
  isValidElement,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { BsPlus } from '@react-icons/all-files/bs/BsPlus';
import { Sticky } from '@bucketplace/common';
import { Tab } from '@bucketplace/design-system';

import { StickyTabTheme, StickyTabThemeContext } from '@/contexts/internal/StickyTabThemeContext';
import { safeColor } from '@/utils/safeColor';

import { EditorPlaceholder } from '../../components';
import { StickyTabInnerContextProvider, useStickyTabInnerContext } from '../../contexts';
import { useActiveTab, useEditorNode } from '../../hooks';
import { DocumentPath } from '../../types';
import { StickyTabItemProps } from './StickyTabItem/StickyTabItem';

/** 스크롤이 멈춘 것으로 간주하는 시간 (ms) */
const SCROLL_SETTLE_DELAY_MS = 150;

export interface StickyTabConfigurableProps {
  /**
   * @default false
   */
  scrollable?: boolean;
  backgroundColor?: string;
  selectedColor?: string;
  defaultColor?: string;
}

export interface StickyTabProps extends StickyTabConfigurableProps {
  children?: ReactNode;
  path: DocumentPath;
}

export function StickyTab(props: StickyTabProps): ReactElement {
  return (
    <StickyTabInnerContextProvider>
      <StickyTabContent {...props} />
    </StickyTabInnerContextProvider>
  );
}

function StickyTabContent(props: StickyTabProps): ReactElement {
  const { children, path, scrollable = false, backgroundColor, selectedColor, defaultColor } = props;
  const scrollRef = useRef<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tabListRef = useRef<HTMLDivElement>(null);

  const targetIds = Children.map(children, (child) => child)
    ?.map((child) => (child as ReactElement<StickyTabItemProps>).props.id ?? '')
    .filter((id) => id.length > 0) ?? [];

  const { onChangeActiveTab } = useStickyTabInnerContext();
  const { activeTab, onChangeTab } = useActiveTab({
    targetAnchorIds: targetIds,
    scrollRef,
    containerRef,
  });
  const { displayMode } = useEditorNode(path);
  const theme = useMemo<StickyTabTheme>(() => ({
    background: safeColor(backgroundColor),
    selected: safeColor(selectedColor),
    default: safeColor(defaultColor),
  }), [backgroundColor, selectedColor, defaultColor]);

  useEffect(() => {
    onChangeActiveTab(activeTab ?? '');
  }, [activeTab, onChangeActiveTab]);

  const scrollActiveTabIntoView = useCallback(() => {
    const tabList = tabListRef.current;
    const activeItem = tabList?.querySelector<HTMLElement>('[data-state="active"]');
    if (!tabList || !activeItem) {
      return;
    }

    const listRect = tabList.getBoundingClientRect();
    const itemRect = activeItem.getBoundingClientRect();
    // iOS는 getBoundingClientRect가 소수점 값을 반환하므로 1px 오차 허용
    if (itemRect.left >= listRect.left - 1 && itemRect.right <= listRect.right + 1) {
      return;
    }

    const scrollLeft = tabList.scrollLeft + itemRect.left - listRect.left - (listRect.width - itemRect.width) / 2;
    tabList.scrollTo({ left: scrollLeft, behavior: 'smooth' });
  }, []);

  /**
   * activeTab이 변경됐을 때 활성화된 탭 아이템이 Tab.List 밖에 있으면 보이도록 가로 스크롤.
   * iOS(WebKit)는 세로 스크롤 진행 중에 가로 smooth 스크롤을 실행하면 애니메이션이 취소되며
   * 탭이 깜빡이는 현상이 있어, 세로 스크롤이 멈춘 뒤에 실행한다. (COMMWEB-7917)
   */
  useEffect(() => {
    const scrollElement = scrollRef.current ?? window;
    let timerId: ReturnType<typeof setTimeout>;

    const schedule = (): void => {
      clearTimeout(timerId);
      timerId = setTimeout(scrollActiveTabIntoView, SCROLL_SETTLE_DELAY_MS);
    };

    schedule();
    scrollElement.addEventListener('scroll', schedule, { passive: true });

    return () => {
      clearTimeout(timerId);
      scrollElement.removeEventListener('scroll', schedule);
    };
  }, [activeTab, scrollActiveTabIntoView]);

  useEffect(() => {
    const canvasScrollerElement = document.getElementById('canvas-scroller');
    if (displayMode === 'view') {
      scrollRef.current = canvasScrollerElement?.parentElement?.parentElement ?? null;
    }
  }, [displayMode]);

  if (!children) {
    return (
      <EditorPlaceholder
        icon={<BsPlus />}
        message="탭을 추가해주세요."
      />
    );
  }

  return (
    <StickyTabThemeContext.Provider value={theme}>
      <StickyTabWrapper ref={containerRef}>
        <Sticky
          type="navigation"
          direction="top"
          offset={0}
          zIndex={100}
        >
          <Tab value={activeTab}>
            <Tab.List
              css={theme.background && css`
                background-Color: ${theme.background};
              `}
              ref={tabListRef}
              mode={scrollable ? 'scrollable' : 'fixed'}
            >
              { Children.map(children, (child) => {
                if (!isValidElement(child)) { return child; }
                return cloneElement(child, { onClick: onChangeTab } as StickyTabItemProps);
              }) }
            </Tab.List>
          </Tab>
        </Sticky>
      </StickyTabWrapper>
    </StickyTabThemeContext.Provider>
  );
}
const StickyTabWrapper = styled.div`
  position: relative;
  display: contents;
`;
